import { Timestamp } from 'firebase/firestore';
import type { AllocationBucketType } from '@/features/Collaborators/Models';

export const AvailabilityStatuses = {
  Simulation: 'Simulation',
  Prospection: 'Prospection',
  Obligation: 'Obligation',
  Completed: 'Completed',
  Cancelled: 'Cancelled',
} as const;

export type AvailabilityStatus =
  (typeof AvailabilityStatuses)[keyof typeof AvailabilityStatuses];

export const AVAILABILITY_STATUS_METADATA: Record<
  AvailabilityStatus,
  { label: string; description: string }
> = {
  Simulation: {
    label: 'Simulação',
    description: 'Cenário hipotético usado para análises e previsões.',
  },
  Prospection: {
    label: 'Prospecção',
    description: 'Necessidade em avaliação, ainda sem confirmação.',
  },
  Obligation: {
    label: 'Obrigação',
    description: 'Compromisso formal da Raro em atender à demanda.',
  },
  Completed: {
    label: 'Concluído',
    description: 'Necessidade finalizada e registrada como atendida.',
  },
  Cancelled: {
    label: 'Cancelada',
    description: 'Necessidade cancelada ou não mais aplicável.',
  },
};

export const AvailabilityNeedTypes = {
  BodyShop: 'BodyShop',
  NewTeam: 'NewTeam',
  ReplacementPermanent: 'ReplacementPermanent',
  ReplacementTemporary: 'ReplacementTemporary',
} as const;

export type AvailabilityNeedType =
  (typeof AvailabilityNeedTypes)[keyof typeof AvailabilityNeedTypes];

export const AVAILABILITY_NEED_TYPE_METADATA: Record<
  AvailabilityNeedType,
  { label: string; description: string }
> = {
  BodyShop: {
    label: 'Vaga Body Shop',
    description: 'Disponibilidade para atuação direta com cliente via body shop.',
  },
  NewTeam: {
    label: 'Formação de Novo Time',
    description: 'Constituição de uma nova equipe para iniciativa específica.',
  },
  ReplacementPermanent: {
    label: 'Substituição Definitiva',
    description: 'Reposição permanente de colaborador existente.',
  },
  ReplacementTemporary: {
    label: 'Substituição Temporária',
    description: 'Reposição temporária para cobrir ausência ou período específico.',
  },
};

export const AvailabilityWorkModels = {
  Onsite: 'Onsite',
  Hybrid: 'Hybrid',
  Remote: 'Remote',
} as const;

export type AvailabilityWorkModel =
  (typeof AvailabilityWorkModels)[keyof typeof AvailabilityWorkModels];

export const AVAILABILITY_WORK_MODEL_METADATA: Record<
  AvailabilityWorkModel,
  { label: string }
> = {
  Onsite: { label: 'Presencial' },
  Hybrid: { label: 'Híbrido' },
  Remote: { label: 'Remoto' },
};

export interface AvailabilityCapability {
  typeId: string;
  typeName: string;
  technologyId?: string | null;
  technologyName?: string | null;
  seniorityId?: string | null;
  seniorityName?: string | null;
  required: boolean;
  isPrimary?: boolean;
}

export interface CapabilityScoreDebugEntry {
  requirement: Record<string, unknown>;
  matchedCapability: Record<string, unknown> | null;
  ratio: number;
  weight: number;
  contribution: number;
  seniorityMet: string;
  block: 'primary' | 'mandatory' | 'optional';
}

export interface CapabilityScoreDebug {
  primary: CapabilityScoreDebugEntry | null;
  mandatory: CapabilityScoreDebugEntry[];
  optional: CapabilityScoreDebugEntry[];
  total: {
    primary: number;
    mandatory: number;
    optional: number;
    overall: number;
  };
}

export interface ScoreBreakdown {
  value: number;
  weight: number;
  label: string;
  assumptions?: Record<string, unknown>;
}

export interface CandidateScoreDebug {
  financial: ScoreBreakdown;
  availability: ScoreBreakdown;
  location: ScoreBreakdown;
  capabilities: CapabilityScoreDebug;
  total: number;
}


export interface AvailabilityCandidate {
  collaboratorId: string;
  collaboratorName: string;
  collaboratorAvatar?: string | null;
  isChosen?: boolean;
  addedAt?: Timestamp;
  matchScore?: number;
  matchScores?: {
    financial: number;
    availability: number;
    location: number;
    capabilities: number;
  };
  matchAssumptions?: {
    salaryMissing: boolean;
    salaryLimitMissing: boolean;
    availabilityMissing: boolean;
    locationFallback: boolean;
    optionalAutoFulfilled: boolean;
  };
  matchInputs?: Record<string, unknown>;
  matchContractType?: string | null;
  matchNormalizedSalary?: number | null;
  matchLocationCity?: string | null;
  matchLocationState?: string | null;
  capabilityDebug?: CapabilityScoreDebug;
  debug?: CandidateScoreDebug;
}

export interface AvailabilitySlot {
  slotId: string;
  title?: string | null;
  bucketType: AllocationBucketType;
  bucketValue: number;
  capabilities: AvailabilityCapability[];
  workModel: AvailabilityWorkModel;
  workCity?: string | null;
  workState?: string | null;
  acceptsPJ: boolean;
  cltSalaryCeiling?: number | null;
  candidates: AvailabilityCandidate[];
  chosenCollaboratorId?: string | null;
  commentsInternal?: string | null;
  lastSuggestionAt?: Timestamp | null;
  lastMatchFilters?: Record<string, unknown> | null;
  lastMatchResultsCount?: number | null;
  fulfillmentStrategy?: 'known' | 'assist' | 'hire';
}

export interface AvailabilityBase {
  clientId: string;
  clientName: string;
  clientLogo?: string | null;
  initiative: string;
  status: AvailabilityStatus;
  needType: AvailabilityNeedType;
  referenceDate?: Timestamp | null;
  notes?: string | null;
  slots: AvailabilitySlot[];
  isInternalDemand?: boolean;
  priorityLevel?: number | null;
  createdAt: Timestamp;
  createdById: string;
  createdByName: string;
  createdByAvatar?: string | null;
  updatedAt?: Timestamp | null;
  updatedById?: string | null;
  updatedByName?: string | null;
  updatedByAvatar?: string | null;
}

export interface AvailabilityDocument extends AvailabilityBase {
  id: string;
}

export type AvailabilityCreate = Omit<
  AvailabilityBase,
  'createdAt' | 'updatedAt'
> & {
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
};

export type AvailabilityUpdate = Partial<
  Omit<AvailabilityBase, 'clientId' | 'clientName' | 'initiative' | 'slots'>
> & {
  id: string;
  clientId?: string;
  clientName?: string;
  initiative?: string;
  slots?: AvailabilitySlot[];
};
